import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAudit } from '../api/audit';
import { formatDate } from '../lib/format';

interface AuditItem {
  actionId: string;
  createdAt: string;
  actorEmail: string;
  action: string;
  targetUserId?: string;
  reason?: string;
  oldValue?: unknown;
  newValue?: unknown;
}

interface AuditTableRowProps {
  a: AuditItem;
  expandedId: string | null;
  onToggle: (id: string) => void;
}

export function AuditTableRow({ a, expandedId, onToggle }: AuditTableRowProps): React.ReactElement {
  return (
    <React.Fragment>
      <tr className="hover:bg-surface-subtle">
        <td className="px-4 py-3 text-text-muted">{formatDate(a.createdAt)}</td>
        <td className="px-4 py-3">{a.actorEmail}</td>
        <td className="px-4 py-3 font-mono text-xs">{a.action}</td>
        <td className="px-4 py-3 font-mono text-xs text-text-muted">{a.targetUserId ?? '—'}</td>
        <td className="px-4 py-3 text-text-secondary">{a.reason ?? '—'}</td>
        <td className="px-4 py-3">
          {(a.oldValue !== undefined || a.newValue !== undefined) && (
            <button
              onClick={() => onToggle(a.actionId)}
              className="text-xs text-brand-600 hover:underline"
            >
              {expandedId === a.actionId ? 'Hide' : 'Show'} changes
            </button>
          )}
        </td>
      </tr>
      {expandedId === a.actionId && (
        <tr>
          <td colSpan={6} className="px-4 py-3 bg-surface-muted">
            <div className="grid grid-cols-2 gap-4 text-xs font-mono">
              <div>
                <p className="text-text-muted mb-1">Before</p>
                <pre>{JSON.stringify(a.oldValue, null, 2)}</pre>
              </div>
              <div>
                <p className="text-text-muted mb-1">After</p>
                <pre>{JSON.stringify(a.newValue, null, 2)}</pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export function AuditLog(): React.ReactElement {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit', { date }],
    queryFn: () => listAudit({ date }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  function handleToggle(id: string): void {
    setExpandedId(expandedId === id ? null : id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Audit Log</h1>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="text-sm border border-border rounded-md px-3 py-1.5"
        />
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle border-b border-border">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Time</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Actor</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Action</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Target</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Reason</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((a) => (
              <AuditTableRow key={a.actionId} a={a} expandedId={expandedId} onToggle={handleToggle} />
            ))}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-text-muted">No audit entries for {date}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
